import { tr } from "../i18n/LanguageContext";
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "../i18n/router";
import { MapPin, Calendar, Users, Loader2, MessageCircle, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import MessageDialog from "../components/MessageDialog";
import { getPracticeLabel } from "../data/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageTarget, setMessageTarget] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/organizers`);
        if (!res.ok) throw new Error(tr("Ошибка загрузки"));
        setOrganizers(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleContact = (org) => {
    if (!user) {
      navigate(`/login?redirect=/organizers`);
      return;
    }
    setMessageTarget(org);
  };

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="eunoia-title-large mb-4">{tr("Наши организаторы")}</h1>
          <p className="eunoia-body-large max-w-2xl mx-auto">
            {tr("Познакомьтесь с опытными мастерами и практиками, которые ведут события в нашем сообществе")}
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-16"><p className="eunoia-body">{error}</p></div>
        )}

        {/* Empty */}
        {!loading && !error && organizers.length === 0 && (
          <div className="eunoia-surface p-12 text-center mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6" style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
              <Users className="h-12 w-12" style={{ color: "var(--eunoia-accent)" }} />
            </div>
            <h3 className="eunoia-title-medium mb-4">{tr("Пока нет организаторов")}</h3>
            <p className="eunoia-body-large mb-6 max-w-lg mx-auto">{tr("Станьте первым организатором духовных практик в нашем сообществе")}</p>
          </div>
        )}

        {/* Organizer Cards */}
        {!loading && !error && organizers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {organizers.map((org) => (
              <div key={org.id} className="eunoia-surface overflow-hidden group hover:-translate-y-1 transition-all duration-300">
                <div className="text-center p-8 pb-4">
                  <div
                    className="w-24 h-24 rounded-full mx-auto mb-5 flex items-center justify-center overflow-hidden cursor-pointer"
                    role="button"
                    tabIndex={0}
                    aria-label={`${tr("Открыть профиль организатора")} ${org.name}`}
                    onClick={() => navigate(`/organizers/${org.userId || org.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/organizers/${org.userId || org.id}`); } }}
                    data-testid={`organizer-avatar-${org.id}`}
                    style={{ backgroundColor: "var(--eunoia-accent-whisper)", border: "3px solid rgba(200,168,130,0.2)" }}
                  >
                    {org.image ? (
                      <img src={org.image} alt={org.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl font-medium" style={{ color: "var(--eunoia-accent)" }}>
                        {(org.name || "?").split(" ").map((n) => n[0]).join("")}
                      </span>
                    )}
                  </div>
                  <h3
                    className="eunoia-title-small mb-2 cursor-pointer hover:underline"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/organizers/${org.userId || org.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/organizers/${org.userId || org.id}`); } }}
                    data-testid={`organizer-name-${org.id}`}
                  >
                    {org.name}
                  </h3>
                  {org.city && (
                    <div className="flex items-center justify-center gap-1.5 eunoia-caption mb-3">
                      <MapPin className="h-3.5 w-3.5" style={{ color: "var(--eunoia-accent)" }} />
                      <span>{org.city}</span>
                    </div>
                  )}
                </div>

                <div className="px-8 pb-8">
                  {org.bio && <p className="eunoia-body mb-5 text-center line-clamp-3">{org.bio}</p>}

                  {org.practices && org.practices.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 justify-center mb-5">
                      {org.practices.map((p, i) => (
                        <span key={i} data-testid={`org-practice-${org.id}-${i}`} className="eunoia-badge text-xs">{getPracticeLabel(p)}</span>
                      ))}
                    </div>
                  )}

                  <div className="space-y-2.5">
                    <button
                      data-testid={`view-events-${org.id}`}
                      onClick={() => navigate(`/organizers/${org.userId || org.id}`)}
                      className="eunoia-btn-primary w-full flex items-center justify-center gap-2 text-sm"
                    >
                      <Calendar className="h-4 w-4" /> {tr("Посмотреть события")} <ArrowRight className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`message-${org.id}`}
                      onClick={() => handleContact(org)}
                      className="eunoia-btn-secondary w-full flex items-center justify-center gap-2 text-sm"
                    >
                      <MessageCircle className="h-4 w-4" /> {tr("Написать организатору")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Become Organizer */}
        <div className="eunoia-surface p-12 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
            <Calendar className="h-10 w-10" style={{ color: "var(--eunoia-accent)" }} />
          </div>
          <h2 className="eunoia-title-medium mb-4">{tr("Станьте организатором")}</h2>
          <p className="eunoia-body-large mb-8 max-w-2xl mx-auto">
            {tr("Поделитесь своими знаниями и опытом с сообществом.")}
          </p>
          <Link
            to={user ? "/organizers/apply" : "/login?redirect=/organizers/apply&intent=organizer_apply"}
            className="eunoia-btn-primary inline-block"
            style={{ textDecoration: "none" }}
            data-testid="organizers-apply-btn"
          >
            {tr("Подать заявку")}
          </Link>
        </div>
      </div>

      {/* Message Dialog */}
      {messageTarget && (
        <MessageDialog organizer={messageTarget} user={user} onClose={() => setMessageTarget(null)} />
      )}
    </div>
  );
};

export default OrganizersPage;
